import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { studyNotesService, slidesService, sessionsService, commentsService } from '../../services/studyNotesService';
import { bibleService } from '../../services/bibleService';
import Icon from '../../components/AppIcon';

// Card/slide themes — replaces old Dark/Light/High Contrast
const SLIDE_THEMES = [
  {
    label: 'Light',
    key: 'light',
    bgClass: 'bg-white',
    textClass: 'text-gray-900',
    style: null,
    refColor: 'text-gray-500',
    navBg: 'bg-white',
  },
  {
    label: 'Dark',
    key: 'dark',
    bgClass: 'bg-gray-950',
    textClass: 'text-gray-100',
    style: null,
    refColor: 'text-gray-400',
    navBg: 'bg-gray-900',
  },
  {
    label: 'High Contrast',
    key: 'high-contrast',
    bgClass: 'bg-black',
    textClass: 'text-yellow-300',
    style: null,
    refColor: 'text-yellow-500',
    navBg: 'bg-black',
  },
  {
    label: 'Royal Gradient',
    key: 'royal',
    bgClass: '',
    textClass: 'text-white',
    style: { background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4338ca 70%, #6d28d9 100%)' },
    refColor: 'text-indigo-300',
    navBg: 'bg-indigo-950',
  },
  {
    label: 'Dawn Glow',
    key: 'dawn',
    bgClass: '',
    textClass: 'text-rose-900',
    style: { background: 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 35%, #fda4af 70%, #f9a8d4 100%)' },
    refColor: 'text-rose-600',
    navBg: 'bg-rose-100',
  },
  {
    label: 'Forest Linen',
    key: 'forest',
    bgClass: '',
    textClass: 'text-white',
    style: { background: 'linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)' },
    refColor: 'text-emerald-300',
    navBg: 'bg-emerald-950',
  },
];

// Slide transition options
const TRANSITIONS = [
  { label: 'Fade In', key: 'fade' },
  { label: 'Slide', key: 'slide' },
  { label: 'Zoom In', key: 'zoom-in' },
  { label: 'Zoom Out', key: 'zoom-out' },
];

// CSS for transitions (injected once) — slide-left = forward, slide-right = backward
const TRANSITION_STYLES = `
@keyframes _pres_fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes _pres_slide_left { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: translateX(0); } }
@keyframes _pres_slide_right { from { opacity: 0; transform: translateX(-60px); } to { opacity: 1; transform: translateX(0); } }
@keyframes _pres_zoom_in { from { opacity: 0; transform: scale(0.82); } to { opacity: 1; transform: scale(1); } }
@keyframes _pres_zoom_out { from { opacity: 0; transform: scale(1.18); } to { opacity: 1; transform: scale(1); } }
._pres_anim_fade { animation: _pres_fade 0.45s ease both; }
._pres_anim_slide-left { animation: _pres_slide_left 0.4s cubic-bezier(.25,.46,.45,.94) both; }
._pres_anim_slide-right { animation: _pres_slide_right 0.4s cubic-bezier(.25,.46,.45,.94) both; }
._pres_anim_zoom-in { animation: _pres_zoom_in 0.4s cubic-bezier(.25,.46,.45,.94) both; }
._pres_anim_zoom-out { animation: _pres_zoom_out 0.4s cubic-bezier(.25,.46,.45,.94) both; }
`;

// 9 font size steps: step 0 = small (~75%), step 8 = ~300%
// ref sizes increased significantly so reference is prominent
const FONT_SIZE_STEPS = [
  { base: 'text-xs',   verse: 'text-base', ref: 'text-sm',   title: 'text-xl'  },
  { base: 'text-sm',   verse: 'text-lg',   ref: 'text-base', title: 'text-2xl' },
  { base: 'text-base', verse: 'text-xl',   ref: 'text-lg',   title: 'text-3xl' },
  { base: 'text-lg',   verse: 'text-2xl',  ref: 'text-xl',   title: 'text-4xl' },
  { base: 'text-xl',   verse: 'text-3xl',  ref: 'text-2xl',  title: 'text-5xl' },
  { base: 'text-2xl',  verse: 'text-4xl',  ref: 'text-3xl',  title: 'text-6xl' },
  { base: 'text-3xl',  verse: 'text-5xl',  ref: 'text-4xl',  title: 'text-7xl' },
  { base: 'text-4xl',  verse: 'text-6xl',  ref: 'text-5xl',  title: 'text-8xl' },
  { base: 'text-5xl',  verse: 'text-7xl',  ref: 'text-6xl',  title: 'text-9xl' },
];

const parseVerseRef = (ref) => {
  if (!ref) return null;
  const match = ref?.trim()?.match(/^(.+?)\s+(\d+):(\d+)(?:-(\d+))?$/);
  if (!match) return null;
  return {
    book: match?.[1],
    chapter: parseInt(match?.[2]),
    verseStart: parseInt(match?.[3]),
    verseEnd: match?.[4] ? parseInt(match?.[4]) : parseInt(match?.[3]),
  };
};

// Remove paragraph markers like ¶ from verse text
const cleanVerseText = (text) => {
  if (!text) return text;
  return text?.replace(/¶\s*/g, '')?.replace(/\s{2,}/g, ' ')?.trim();
};

// Render verse text with KJV markup:
// <text> = red (words of Christ), [text] = italic
const renderVerseText = (rawText) => {
  const text = cleanVerseText(rawText);
  if (!text) return null;
  const parts = [];
  let i = 0;
  let key = 0;
  while (i < text?.length) {
    if (text?.[i] === '<') {
      const end = text?.indexOf('>', i);
      if (end !== -1) {
        parts?.push(
          <span key={key++} className="text-red-400 font-medium">
            {text?.slice(i + 1, end)}
          </span>
        );
        i = end + 1;
        continue;
      }
    }
    if (text?.[i] === '[') {
      const end = text?.indexOf(']', i);
      if (end !== -1) {
        parts?.push(
          <em key={key++} className="italic opacity-80">
            {text?.slice(i + 1, end)}
          </em>
        );
        i = end + 1;
        continue;
      }
    }
    let j = i + 1;
    while (j < text?.length && text?.[j] !== '<' && text?.[j] !== '[') j++;
    parts?.push(<span key={key++}>{text?.slice(i, j)}</span>);
    i = j;
  }
  return parts;
};

const PresenterView = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [note, setNote] = useState(null);
  const [slides, setSlides] = useState([]);
  const [session, setSession] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [comments, setComments] = useState([]);
  const [showQA, setShowQA] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [timerActive, setTimerActive] = useState(false);
  const [timerRemaining, setTimerRemaining] = useState(null);
  // Slide theme key (replaces old light/dark/high-contrast)
  const [slideThemeKey, setSlideThemeKey] = useState('light');
  // Transition key
  const [transitionKey, setTransitionKey] = useState('fade');
  // Animation trigger: increments on slide change to re-trigger animation
  const [animTick, setAnimTick] = useState(0);
  // Direction of last slide change: 'forward' or 'backward'
  const [slideDirection, setSlideDirection] = useState('forward');
  const [fontSizeStep, setFontSizeStep] = useState(2);
  const [verseCache, setVerseCache] = useState({});

  const timerRef = useRef(null);
  const channelRef = useRef(null);
  const commentChannelRef = useRef(null);
  const containerRef = useRef(null);

  // Inject transition CSS once
  useEffect(() => {
    if (!document.getElementById('_pres_transition_styles')) {
      const style = document.createElement('style');
      style.id = '_pres_transition_styles';
      style.textContent = TRANSITION_STYLES;
      document.head?.appendChild(style);
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef?.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  useEffect(() => {
    loadData();
    return () => {
      if (channelRef?.current) channelRef?.current?.unsubscribe();
      if (commentChannelRef?.current) commentChannelRef?.current?.unsubscribe();
      clearInterval(timerRef?.current);
    };
  }, [slug]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (!slides?.length) return;
    const toFetch = [currentIndex, currentIndex + 1]?.filter(i => i >= 0 && i < slides?.length);
    toFetch?.forEach(i => {
      const slide = slides?.[i];
      if (slide?.slideType === 'verse' && slide?.verseReference) {
        const version = slide?.versionOverride || note?.default_version || 'KJV';
        const cacheKey = `${slide?.verseReference}|${version}`;
        if (!verseCache?.[cacheKey]) {
          fetchVerseText(slide?.verseReference, version, cacheKey);
        }
      }
    });
  }, [currentIndex, slides, note]);

  const fetchVerseText = async (ref, version, cacheKey) => {
    const parsed = parseVerseRef(ref);
    if (!parsed) return;
    try {
      const { data } = await bibleService?.getChapterVerses(parsed?.book, parsed?.chapter, version);
      if (data && data?.length > 0) {
        const verses = data?.filter(v => v?.verse_number >= parsed?.verseStart && v?.verse_number <= parsed?.verseEnd);
        const text = verses?.map(v => v?.text)?.join(' ');
        if (text) {
          setVerseCache(prev => ({ ...prev, [cacheKey]: text }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch verse:', err);
    }
  };

  const getVerseText = (slide) => {
    if (!slide?.verseReference) return null;
    const version = slide?.versionOverride || note?.default_version || 'KJV';
    const cacheKey = `${slide?.verseReference}|${version}`;
    return verseCache?.[cacheKey] || null;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const noteData = await studyNotesService?.getNoteBySlug(slug);
      if (!noteData) { setError('Note not found'); return; }
      if (noteData?.created_by !== user?.id) { setError('You do not have permission to present this note'); return; }

      setNote(noteData);

      // Map legacy font_size string to step index
      const fsMap = { small: 1, medium: 2, large: 4 };
      setFontSizeStep(fsMap?.[noteData?.font_size] ?? 2);

      // Load card background from localStorage and map to slide theme
      const savedBgMap = JSON.parse(localStorage.getItem('_fm_card_bg_map') || '{}');
      const bgIdx = savedBgMap?.[noteData?.id] || 0;
      const bgToTheme = ['light', 'royal', 'dawn', 'forest'];
      setSlideThemeKey(bgToTheme?.[bgIdx] || 'light');

      const slideData = await slidesService?.getSlides(noteData?.id);
      setSlides(slideData);

      const sessionData = await sessionsService?.getOrCreateSession(noteData?.id);
      setSession(sessionData);
      setCurrentIndex(sessionData?.current_slide_index || 0);

      const commentData = await commentsService?.getComments(sessionData?.id);
      setComments(commentData);

      commentChannelRef.current = commentsService?.subscribeToComments(sessionData?.id, (newComment) => {
        setComments(prev => [...prev, newComment]);
      });
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const pushSlide = useCallback(async (index) => {
    const direction = index >= currentIndex ? 'forward' : 'backward';
    setSlideDirection(direction);
    setCurrentIndex(index);
    setAnimTick(t => t + 1);
    if (session?.id && note?.sync_mode === 'live') {
      try {
        await sessionsService?.broadcastSlideChange(session?.id, index, true);
        await sessionsService?.updateSession(session?.id, {
          current_slide_index: index,
          is_live: true,
        });
      } catch (err) {
        console.error('Failed to push slide:', err);
      }
    }
    const slide = slides?.[index];
    if (slide?.slideTimerSeconds) {
      startTimer(slide?.slideTimerSeconds);
    } else {
      clearInterval(timerRef?.current);
      setTimerActive(false);
      setTimerRemaining(null);
    }
  }, [session, note, slides, currentIndex]);

  const startTimer = (seconds) => {
    clearInterval(timerRef?.current);
    setTimerRemaining(seconds);
    setTimerActive(true);
    timerRef.current = setInterval(() => {
      setTimerRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef?.current);
          setTimerActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleNext = () => {
    if (currentIndex < slides?.length - 1) pushSlide(currentIndex + 1);
  };

  const handlePrev = () => {
    if (currentIndex > 0) pushSlide(currentIndex - 1);
  };

  const toggleLive = async () => {
    if (!session) return;
    try {
      const updated = await sessionsService?.updateSession(session?.id, { is_live: !session?.is_live });
      setSession(updated);
    } catch (err) {
      console.error(err);
    }
  };

  const toggleQA = async () => {
    if (!session) return;
    try {
      const updated = await sessionsService?.updateSession(session?.id, { qa_enabled: !session?.qa_enabled });
      setSession(updated);
    } catch (err) {
      console.error(err);
    }
  };

  const regenerateCode = async () => {
    if (!session) return;
    try {
      const updated = await sessionsService?.regenerateJoinCode(session?.id);
      setSession(updated);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await commentsService?.deleteComment(commentId);
      setComments(prev => prev?.filter(c => c?.id !== commentId));
    } catch (err) {
      console.error(err);
    }
  };

  const currentSlide = slides?.[currentIndex];

  const formatTimer = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s?.toString()?.padStart(2, '0')}`;
  };

  const getSlideSummary = (slide, index) => {
    if (!slide) return `Slide ${index + 1}`;
    if (slide?.slideType === 'title') return `${index + 1}: Title — ${slide?.textContent?.slice(0, 30) || note?.title?.slice(0, 30) || 'Title'}`;
    if (slide?.slideType === 'verse') return `${index + 1}: ${slide?.verseReference || 'Verse'}`;
    if (slide?.slideType === 'text') return `${index + 1}: ${slide?.textContent?.slice(0, 40) || 'Text'}...`;
    return `Slide ${index + 1}`;
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e?.key === 'ArrowRight' || e?.key === 'ArrowDown') handleNext();
      if (e?.key === 'ArrowLeft' || e?.key === 'ArrowUp') handlePrev();
      if (e?.key === 'Escape' && !document.fullscreenElement) setIsFullscreen(false);
      if (e?.key === 'f' || e?.key === 'F') toggleFullscreen();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentIndex, slides?.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          <button onClick={() => navigate('/tools/notes')} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm">
            Back to Notes
          </button>
        </div>
      </div>
    );
  }

  const slideTheme = SLIDE_THEMES?.find(t => t?.key === slideThemeKey) || SLIDE_THEMES?.[0];
  const fs = FONT_SIZE_STEPS?.[fontSizeStep] || FONT_SIZE_STEPS?.[2];

  // Determine animation class based on transition type and direction
  const getAnimClass = () => {
    if (transitionKey === 'slide') {
      return slideDirection === 'forward' ? '_pres_anim_slide-left' : '_pres_anim_slide-right';
    }
    return `_pres_anim_${transitionKey}`;
  };
  const animClass = getAnimClass();

  return (
    <div
      ref={containerRef}
      className={`min-h-screen flex flex-col ${isFullscreen && !document.fullscreenElement ? 'fixed inset-0 z-50' : ''} bg-background`}
    >
      {/* Top Controls */}
      {(!isFullscreen || !document.fullscreenElement) && (
        <div className="bg-card border-b border-border px-4 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/tools/notes')} className="p-1.5 hover:bg-muted rounded-lg transition-colors">
              <Icon name="ArrowLeft" size={18} />
            </button>
            <div>
              <h1 className="text-sm font-semibold text-foreground">{note?.title}</h1>
              <p className="text-xs text-muted-foreground">{note?.speaker}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Slide Jump-To Selector */}
            <select
              value={currentIndex}
              onChange={e => pushSlide(parseInt(e?.target?.value))}
              className="px-2 py-1.5 bg-background border border-border rounded-lg text-xs focus:outline-none max-w-[180px] truncate"
              title="Jump to slide"
            >
              {slides?.map((slide, i) => (
                <option key={i} value={i}>{getSlideSummary(slide, i)}</option>
              ))}
            </select>

            {/* Slide Transition */}
            <select
              value={transitionKey}
              onChange={e => setTransitionKey(e?.target?.value)}
              className="px-2 py-1.5 bg-background border border-border rounded-lg text-xs focus:outline-none"
              title="Slide transition"
            >
              {TRANSITIONS?.map(t => (
                <option key={t?.key} value={t?.key}>{t?.label}</option>
              ))}
            </select>

            {/* Slide Theme (replaces old Dark/Light/High Contrast) */}
            <select
              value={slideThemeKey}
              onChange={e => setSlideThemeKey(e?.target?.value)}
              className="px-2 py-1.5 bg-background border border-border rounded-lg text-xs focus:outline-none"
              title="Slide theme"
            >
              {SLIDE_THEMES?.map(t => (
                <option key={t?.key} value={t?.key}>{t?.label}</option>
              ))}
            </select>

            {/* Font size T-/T+ buttons */}
            <div className="flex items-center gap-1 bg-background border border-border rounded-lg px-1 py-0.5">
              <button
                onClick={() => setFontSizeStep(s => Math.max(0, s - 1))}
                disabled={fontSizeStep === 0}
                className="flex items-center gap-0.5 px-1.5 py-1 rounded hover:bg-muted disabled:opacity-30 transition-colors"
                title="Decrease text size"
              >
                <Icon name="Minus" size={11} />
                <span className="text-xs font-bold leading-none">T</span>
              </button>
              <div className="w-px h-4 bg-border" />
              <button
                onClick={() => setFontSizeStep(s => Math.min(8, s + 1))}
                disabled={fontSizeStep === 8}
                className="flex items-center gap-0.5 px-1.5 py-1 rounded hover:bg-muted disabled:opacity-30 transition-colors"
                title="Increase text size"
              >
                <Icon name="Plus" size={11} />
                <span className="text-base font-bold leading-none">T</span>
              </button>
            </div>

            {/* Live toggle */}
            {note?.sync_mode === 'live' && (
              <button
                onClick={toggleLive}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  session?.is_live ? 'bg-red-500 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <div className={`w-2 h-2 rounded-full ${session?.is_live ? 'bg-white animate-pulse' : 'bg-muted-foreground'}`} />
                {session?.is_live ? 'Live' : 'Go Live'}
              </button>
            )}

            {/* Q&A on/off toggle */}
            <button
              onClick={toggleQA}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                session?.qa_enabled ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}
              title={session?.qa_enabled ? 'Q&A is On — click to turn off' : 'Q&A is Off — click to turn on'}
            >
              <Icon name="MessageSquare" size={13} />
              Q&A {session?.qa_enabled ? 'On' : 'Off'}
            </button>

            {/* Show/Hide Q&A Panel */}
            <button
              onClick={() => { setShowQA(!showQA); setShowNotes(false); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                showQA ? 'bg-blue-100 text-blue-700' : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              title={showQA ? 'Hide Q&A panel' : 'Show Q&A panel'}
            >
              <Icon name="MessageCircle" size={13} />
              {comments?.length}
            </button>

            {/* Presenter Notes toggle */}
            <button
              onClick={() => { setShowNotes(!showNotes); setShowQA(false); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                showNotes ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              title={showNotes ? 'Hide presenter notes' : 'Show presenter notes'}
            >
              <Icon name="FileText" size={13} />
              Notes
            </button>

            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="p-1.5 hover:bg-muted rounded-lg transition-colors"
              title="Toggle fullscreen (F)"
            >
              <Icon name="Maximize2" size={16} />
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
        {/* Main Slide Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Current Slide */}
          <div
            className={`flex-1 flex items-center justify-center px-16 py-12 ${slideTheme?.bgClass} ${slideTheme?.textClass}`}
            style={slideTheme?.style || {}}
          >
            {currentSlide ? (
              <div
                key={`${currentIndex}-${animTick}`}
                className={`max-w-4xl w-full text-center ${animClass}`}
              >
                {currentSlide?.slideType === 'title' && (
                  <div className="px-8">
                    <p className="text-xs uppercase tracking-widest opacity-50 mb-6">
                      {note?.church_name || 'Study Notes'}
                    </p>
                    <h1 className={`${fs?.title} font-bold mb-6 leading-tight`}>{currentSlide?.textContent || note?.title}</h1>
                    {note?.speaker && <p className={`${fs?.base} opacity-70 mt-4`}>{note?.speaker}</p>}
                  </div>
                )}

                {currentSlide?.slideType === 'verse' && (
                  <div className="px-8">
                    {/* Reference at TOP with subtle color */}
                    <p className={`${fs?.ref} uppercase tracking-widest mb-8 font-medium ${slideTheme?.refColor}`}>
                      {currentSlide?.verseReference}
                      {(currentSlide?.versionOverride || note?.default_version) && (
                        <span className="ml-2 opacity-70">— {currentSlide?.versionOverride || note?.default_version || 'KJV'}</span>
                      )}
                    </p>
                    {/* Verse text large */}
                    {getVerseText(currentSlide) ? (
                      <p className={`${fs?.verse} leading-relaxed italic`}>
                        "{renderVerseText(getVerseText(currentSlide))}"
                      </p>
                    ) : (
                      <p className={`${fs?.base} opacity-50 italic`}>Loading verse text…</p>
                    )}
                  </div>
                )}

                {currentSlide?.slideType === 'text' && (
                  <div className="px-8">
                    {/* Text slides use verse font size for same relative size */}
                    <p className={`${fs?.verse} leading-relaxed`}>{currentSlide?.textContent}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="opacity-50">No slides</p>
            )}
          </div>

          {/* Timer */}
          {timerActive && timerRemaining !== null && (
            <div className={`text-center py-2 ${timerRemaining <= 10 ? 'bg-red-500/20 text-red-600' : 'bg-muted'}`}>
              <span className="text-2xl font-mono font-bold">{formatTimer(timerRemaining)}</span>
            </div>
          )}

          {/* Navigation Controls */}
          <div
            className={`border-t border-white/10 px-6 py-4 flex items-center justify-between ${slideTheme?.textClass}`}
            style={slideTheme?.style ? { ...slideTheme?.style, backgroundSize: '200% 200%', backgroundPosition: 'bottom' } : { backgroundColor: 'var(--color-card)' }}
          >
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm disabled:opacity-30 transition-colors"
            >
              <Icon name="ChevronLeft" size={16} />
              Previous
            </button>

            <div className="flex items-center gap-3">
              <span className="text-sm opacity-70">
                {currentIndex + 1} / {slides?.length}
              </span>
              {/* Slide dots */}
              <div className="flex gap-1">
                {slides?.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => pushSlide(i)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      i === currentIndex ? 'bg-current w-4' : 'bg-current opacity-30'
                    }`}
                  />
                ))}
              </div>
            </div>

            <button
              onClick={handleNext}
              disabled={currentIndex === slides?.length - 1}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm disabled:opacity-30 transition-colors"
            >
              Next
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        </div>

        {/* Q&A Panel */}
        {showQA && (
          <div className="w-80 bg-card border-l border-border flex flex-col shrink-0">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Q&A Panel</h3>
              <button onClick={() => setShowQA(false)} className="p-1 hover:bg-muted rounded">
                <Icon name="X" size={16} />
              </button>
            </div>

            {/* Join Code */}
            <div className="p-4 bg-muted/50 border-b border-border">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Join Code</span>
                <button onClick={regenerateCode} className="text-xs text-primary hover:underline">Regenerate</button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-mono font-bold text-foreground tracking-widest">
                  {session?.qa_join_code || '----'}
                </span>
                <div className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
                  session?.qa_enabled ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'
                }`}>
                  {session?.qa_enabled ? 'Open' : 'Closed'}
                </div>
              </div>
            </div>

            {/* Comments */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {comments?.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-8">No questions yet</p>
              ) : (
                comments?.map(comment => (
                  <div key={comment?.id} className="bg-background rounded-lg p-3 border border-border">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground mb-1">
                          {comment?.display_name || 'Anonymous'}
                        </p>
                        <p className="text-sm text-foreground">{comment?.content}</p>
                      </div>
                      <button
                        onClick={() => deleteComment(comment?.id)}
                        className="p-1 hover:bg-destructive/10 rounded shrink-0"
                      >
                        <Icon name="X" size={12} color="var(--color-destructive)" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Presenter Notes Panel */}
        {showNotes && (
          <div className="w-80 bg-card border-l border-border flex flex-col shrink-0">
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/40">
              <div className="flex items-center gap-2">
                <Icon name="FileText" size={15} color="var(--color-primary)" />
                <h3 className="text-sm font-semibold text-foreground">Presenter Notes</h3>
              </div>
              <button onClick={() => setShowNotes(false)} className="p-1 hover:bg-muted rounded">
                <Icon name="X" size={16} />
              </button>
            </div>

            {/* Slide context */}
            <div className="p-3 bg-muted/20 border-b border-border">
              <p className="text-xs text-muted-foreground font-medium">
                Slide {currentIndex + 1} of {slides?.length}
                {currentSlide?.slideType === 'verse' && currentSlide?.verseReference && (
                  <span className="ml-2 font-bold text-foreground">— {currentSlide?.verseReference}</span>
                )}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {currentSlide?.presenterNotes ? (
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                  {currentSlide?.presenterNotes}
                </p>
              ) : (
                <div className="text-center py-8">
                  <Icon name="FileText" size={28} color="var(--color-muted-foreground)" className="mx-auto mb-2 opacity-40" />
                  <p className="text-xs text-muted-foreground opacity-70">No notes for this slide</p>
                  <p className="text-xs text-muted-foreground mt-1 opacity-50">Add notes in the editor</p>
                </div>
              )}
            </div>

            {/* All slides notes overview */}
            {slides?.some(s => s?.presenterNotes) && (
              <div className="border-t border-border p-3">
                <p className="text-xs text-muted-foreground font-medium mb-2">All Slide Notes</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {slides?.map((slide, i) => slide?.presenterNotes ? (
                    <button
                      key={i}
                      onClick={() => pushSlide(i)}
                      className={`w-full text-left p-2 rounded-lg text-xs transition-colors ${
                        i === currentIndex ? 'bg-primary/10 text-primary' : 'bg-muted text-foreground hover:bg-muted/80'
                      }`}
                    >
                      <span className="font-medium">Slide {i + 1}:</span>{' '}
                      {slide?.presenterNotes?.slice(0, 50)}{slide?.presenterNotes?.length > 50 ? '…' : ''}
                    </button>
                  ) : null)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Fullscreen exit overlay */}
      {(isFullscreen || document.fullscreenElement) && (
        <button
          onClick={toggleFullscreen}
          className="fixed top-4 right-4 z-50 p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors"
          title="Exit fullscreen"
        >
          <Icon name="Minimize2" size={18} />
        </button>
      )}
    </div>
  );
};

export default PresenterView;
